import { Effect, Result, Schema as S } from "effect";
import * as Command from "foldkit/command";

import { downloadEntryFile } from "../api/commands/entries";
import { ApiError } from "../api/errors";

/**
 * v1 `Entry.tsx:303-332`: fetch the upload blob, then save it through a
 * temporary anchor (KTD6: no `foldkit/file`). The typed `ApiError` reaches
 * `update` as data; the anchor click is the only DOM side effect.
 */

export const saveBlob = (blob: Blob, fileName: string): void => {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = fileName;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
};

export const DownloadResult = S.Result(S.Boolean, ApiError);

type Args = Readonly<{
	knotId: string;
	fileId: string;
	/** Fallback when the response carries no `Content-Disposition` name. */
	fileName: string;
	generation: number;
}>;

/** One Command per page Message, as `defineApi(...).command`. */
export const defineDownload = <Msg extends S.Top>(
	message: Msg,
	toMessage: (
		payload: Args & { result: typeof DownloadResult.Type },
	) => S.Schema.Type<Msg>,
) =>
	Command.define("DownloadEntryFile", {
		args: {
			knotId: S.String,
			fileId: S.String,
			fileName: S.String,
			generation: S.Number,
		},
		messages: [message],
		execute: (args) =>
			downloadEntryFile.run({ knotId: args.knotId, fileId: args.fileId }).pipe(
				Effect.map((result) => {
					Result.map(result, (file) =>
						saveBlob(file.blob, file.fileName ?? args.fileName),
					);
					return toMessage({ ...args, result: Result.map(result, () => true) });
				}),
			),
	});
